import { FC, Fragment, useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { HashRouter as Router, useHistory, useLocation } from "react-router-dom";
import { useResponsiveMode } from "@fluentui/react";
import { LoadingOverlay, useComponentLogger } from "cayo.ui";
import { ThemeProvider } from "styled-components";
import { NavTreeClient, DashboardClient } from "@cayo/shared/api/schema";
import { IScheme } from "@cayo/shared/api/types/schema";
import UserProfileClient from "@cayo/shared/api/UserProfileClient";
import { IAlertItem } from "@cayo/shared/components/Alerts/types";
import { AppContext } from "@cayo/shared/components/App/appContext";
import { appEvents } from "@cayo/shared/components/App/appEvents";
import { appReducer, useSchemes } from "@cayo/shared/components/App/hooks";
import {
  IUserMessage,
  IAppEvents,
  RightPanelType,
  ServerError,
} from "@cayo/shared/components/App/types";
import { appSignals, appUtils } from "@cayo/shared/components/App/utils";
import { HealthCheckClient } from "@cayo/shared/components/AppState/api/healthCheck";
import ConfirmationModal from "@cayo/shared/components/dialogs/ConfirmationModal";
import { useUIInteraction } from "@cayo/shared/hooks/useUIInteraction";
import { localeStore } from "@cayo/shared/i18n/locale-store";
import { commonMessages } from "@cayo/shared/messages/common";
import { endpoints } from "@cayo/shared/services/endpoints.service";
import notificationService, { mergeInteractiveItems } from "@cayo/shared/services/notifications";
import ServicesContext, { IServices } from "@cayo/shared/services/servicesContext";
import tokenStorage from "@cayo/shared/settings/tokenStorage";
import viewStorageFactory from "@cayo/shared/settings/viewStorage";
import { objectUtils } from "@cayo/shared/utils/object";
import GlobalStyle, { GridStyle } from "./GlobalStyle";
import { ActionBuilder } from "../../scheme/ActionBuilder/ActionBuilder";
import { getDialogRegistry } from "../../services/Dialogs/dialog-registry";
import { useServerTheme } from "../../themes";
import { getAppId } from "../../utils/app-ids";
import ServerStateComponent from "../AppState/ServerStateComponent";
import DialogProvider from "../DialogProvider";
import ErrorModal from "../Dialogs/ErrorModal";
import globalHooks from "../GlobalHooks";
import { useManifest } from "../GlobalHooks/useManifest";
import AppRouting from "../routing";

const LocalizedApp: FC = () => {
  const rootRef = useRef(null);
  const intl = useIntl();
  const log = useComponentLogger(LocalizedApp);
  const history = useHistory();
  const location = useLocation();
  const uiInteraction = useUIInteraction(log);
  const [isSigningIn, setIsSigningIn] = useState(false);

  const newResponsiveMode = useResponsiveMode(rootRef);

  const manifest = useManifest();

  const services = useMemo<IServices>(
    () => ({
      actionBuilder: new ActionBuilder(intl, { history, location }, uiInteraction),
      uiInteraction,

      tokenStorage,
      viewStorageFactory,

      healthCheck: new HealthCheckClient(endpoints.publicUrl),
      notificationService,

      navTreeClient: new NavTreeClient(endpoints.publicUrl),
      userProfileClient: new UserProfileClient<IScheme>(endpoints.publicUrl),
      dashboardClient: new DashboardClient(endpoints.publicUrl),
    }),
    []
  );

  const isLoginPathname = location.pathname === "/login";

  useEffect(() => {
    appSignals.subscribe("signingIn", (data) => setIsSigningIn(!!data?.signingIn));
  }, []);

  useEffect(() => {
    if (appUtils.isStaticPage()) {
      log.debug("static page. notification service will not start");
      return;
    }

    const notificationsDisabled =
      !manifest ||
      (manifest.client_config?.features &&
        manifest.client_config?.features["notifications"] === "disabled");

    if (notificationsDisabled) {
      return;
    }

    notificationService.start();

    window.addEventListener("beforeunload", () => {
      notificationService.stop();
    });

    return () => {
      //  document.removeEventListener("mousedown", onMouseDown);

      notificationService.stop();
    };
  }, [manifest]);

  const {
    propertiesPanels,
    clientComponents,
    hideClientComponents,
    removeClientComponent,
    showControl,
    showAction,
    isLoading: isSchemeLoading,
    updatePropertiesPanel,
    closePropertiesPanel,
  } = useSchemes();

  const { userSettings, setUserSettings } = globalHooks.useUserSettings();
  const hideConfirmation = () => {
    setState({ confirmationMessage: undefined });
  };

  const setBreadcrumbInfo = (info: any[] | undefined): void => {
    setState({ breadcrumbInfo: info });
  };

  const setFirstAlertButtonId = (id: RightPanelType): void => {
    setState({ firstAlertButtonId: id });
  };

  const setLocale = (locale: string): void => {
    localeStore.setLocale(locale);
    setUserSettings({ lang: locale });
  };

  const setStaticAlerts = (alerts: IAlertItem[] | undefined): void => {
    if (alerts) {
      const newAertItems = (state.staticAlerts && [...state.staticAlerts]) || [];
      mergeInteractiveItems(
        newAertItems,
        alerts,
        true,
        (alert) => (alert as IAlertItem)?.status !== "new"
      );

      setState({ staticAlerts: newAertItems.filter((a: any) => a.status === "new") });
    }
  };

  const showConfirmation = (
    confirmationMessage: string | undefined,
    onConfirmed: () => Promise<void>,
    confirmationWarning?: string | undefined
  ): void => {
    setState({ confirmationMessage, onConfirmed, confirmationWarning });
  };

  const showError = (error: string | undefined): void => {
    // TODO:
    if (error === "invalid_grant") {
      return;
    }

    setState({ message: { text: error || "Unknown error", severity: "error" } });
  };

  const showMessage = (message: IUserMessage | undefined): void => {
    // TODO:
    if (message?.text === "invalid_grant") {
      return;
    }

    setState({ message });
  };

  const showRightPanel = (rightPanelType?: RightPanelType): void => {
    setState({ rightPanelType });
  };

  const showServerUnavailable = (serverError: ServerError | null | undefined): void => {
    log.debug("showServerUnavailable", serverError);
    setState({ serverError });
  };

  useEffect(() => {
    setState({ responsiveMode: newResponsiveMode });
  }, [newResponsiveMode]);

  const [state, setState] = useReducer(appReducer, {
    breadcrumbInfo: [],
    hideConfirmation,
    responsiveMode: newResponsiveMode,
    setLocale,
    showConfirmation,

    rightPanelType: undefined,
    showRightPanel,
    setFirstAlertButtonId,
    setBreadcrumbInfo,
    setStaticAlerts,
    showError,
    showMessage,

    showServerUnavailable,
  });

  const isServerAvailable = state.serverError === null;

  useEffect(() => {
    const handleAppEventsHander = (evt?: IAppEvents | undefined) => {
      if (!evt) {
        return;
      }

      if (evt.hideClientComponent) {
        hideClientComponents();
        return;
      }

      if (evt.showControl !== undefined) {
        showControl(evt.showControl);
        return;
      }

      if (evt.action !== undefined) {
        showAction(evt.action);
        return;
      }

      if (evt.showError !== undefined) {
        showError(evt.showError);
        return;
      }

      if (evt.showMessage !== undefined) {
        showMessage(evt.showMessage.text ? evt.showMessage : undefined);
        return;
      }

      if (evt.webSocketConnectionLost !== undefined) {
        setState({ webSockerConnectionLost: evt.webSocketConnectionLost });
        if (evt.webSocketConnectionLost === false && !appUtils.isStaticPage()) {
          // showServerUnavailable(false);
          if (!/^#\/login\?/i.test(document.location.href) && state.serverError) {
            log.debug(
              "reload page, webSocketConnectionLost==false, serverError: ",
              state.serverError
            );
            document.location.reload();
          }
        }
        return;
      }

      if (evt.serverError !== undefined) {
        showServerUnavailable(evt.serverError);
        return;
      }

      if (evt.updateProps) {
        if (evt.updateProps.objects?.length === 1) {
          updatePropertiesPanel({
            id: evt.updateProps.objects[0].objectPath,
            parentId: evt.updateProps.id,
          });
        } else {
          closePropertiesPanel(evt.updateProps.id);
        }
      }
    };

    return appEvents.on(handleAppEventsHander);
  }, [clientComponents, propertiesPanels, state.serverError]);

  const renderClientComponents = useCallback(() => {
    return (
      <Fragment>
        {clientComponents?.map((clientComponent, i) => {
          const onClose = (result: boolean) => {
            removeClientComponent();
            if (clientComponent?.parameters?.onClose) {
              clientComponent.parameters.onClose(result);
            }

            if (result && clientComponent?.parameters?.reloadAfterClose) {
              log.debug("reload page, reloadAfterClose");
              document.location.reload();
            } else if (result !== false && (clientComponent?.parameters as any).onRefresh) {
              (clientComponent?.parameters as any).onRefresh();
            }
          };

          const componentProps = {
            ...clientComponent.parameters,
            ...(objectUtils.cloneObject(clientComponent.parameters) || {}),
            onClose,
          };

          const ClientComponent = clientComponent.component as any;
          return (
            <ClientComponent
              {...componentProps}
              key={
                (clientComponent?.parameters as any)?.url ||
                clientComponent?.component?.displayName ||
                `clientCmp${i}`
              }
            />
          );
        })}
      </Fragment>
    );
  }, [clientComponents]);

  useEffect(() => {
    setState({ manifest });
  }, [manifest]);

  useEffect(() => {
    const manifestDefaultTheme = manifest?.client_config?.default_theme;

    if (userSettings.theme || !manifestDefaultTheme) {
      return;
    }

    setUserSettings({ ...userSettings, theme: manifestDefaultTheme });
  }, [manifest, setUserSettings, userSettings]);

  // TODO: improve theme selection logic!
  const defaultTheme = userSettings.theme || manifest?.client_config?.default_theme || "default";
  const knownThemes = useMemo(() => manifest?.client_config?.themes || [], [manifest]);
  const { theme, isLoading: isThemeLoading } = useServerTheme(defaultTheme, knownThemes);
  const appId = getAppId(manifest);
  const dialogRegistry = useMemo(() => getDialogRegistry(appId), [appId]);

  if (!manifest) {
    return <LoadingOverlay text={intl.formatMessage(commonMessages.loading)} />;
  }

  if (isThemeLoading) {
    return <LoadingOverlay text={intl.formatMessage(commonMessages.loadingThemes)} />;
  }

  const isMessageVisible = isServerAvailable && state.message && !isLoginPathname && !isSigningIn;

  return (
    <ServicesContext.Provider value={services}>
      <ThemeProvider theme={theme}>
        <GlobalStyle {...theme} />
        <GridStyle />

        {isMessageVisible && (
          <ErrorModal message={state.message!} onDismiss={() => showMessage(undefined)} />
        )}
        {isServerAvailable && state.confirmationMessage && (
          <ConfirmationModal
            onClose={state.hideConfirmation}
            onConfirmed={state.onConfirmed}
            confirmationMessage={state.confirmationMessage}
            confirmationWarning={state.confirmationWarning}
            showError={state.showError}
            objects={(state as any)?.objects}
          />
        )}

        <AppContext.Provider value={state}>
          <DialogProvider dialogs={dialogRegistry}>
            {/* ServerStateComponent must always be created to listen for app events */}
            <ServerStateComponent
              onServerUnavailable={showServerUnavailable}
              serverError={state.serverError}
            />

            {isServerAvailable && (
              <>
                {renderClientComponents()}
                <Router>
                  {uiInteraction.loadingInfo && (
                    <LoadingOverlay
                      text={
                        typeof uiInteraction.loadingInfo === "string"
                          ? uiInteraction.loadingInfo
                          : undefined
                      }
                    />
                  )}
                  <AppRouting>{isSchemeLoading && <LoadingOverlay />}</AppRouting>
                </Router>
              </>
            )}
          </DialogProvider>
        </AppContext.Provider>
      </ThemeProvider>
    </ServicesContext.Provider>
  );
};

export default LocalizedApp;
